import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, TextField, CircularProgress, Alert } from "@mui/material";
import { Save } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import systemSettingsService from "../../services/systemSettingsService";
import PageHeader from "../../components/common/PageHeader";

// Third-party tracking/analytics snippets — pasted in exactly as the
// provider (Google Tag Manager, an ad platform, etc.) hands them out, with
// no parsing/validation. Only ever injected on the PUBLIC pages
// (Login/Register/etc — see client/src/layouts/AuthLayout.jsx), never on
// an authenticated admin/customer page. The admin pasting this IS the
// trust boundary, same as any CMS "custom code" field.
const AdminIntegrations = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [integrations, setIntegrations] = useState({
        gtmHeadSnippet: "",
        gtmBodySnippet: "",
        leadConversionSnippet: "",
    });

    useEffect(() => {
        systemSettingsService
            .get()
            .then(({ data }) =>
                setIntegrations({
                    gtmHeadSnippet: data.data.settings?.integrations?.gtmHeadSnippet || "",
                    gtmBodySnippet: data.data.settings?.integrations?.gtmBodySnippet || "",
                    leadConversionSnippet: data.data.settings?.integrations?.leadConversionSnippet || "",
                }),
            )
            .catch(() => enqueueSnackbar("Failed to load integrations", { variant: "error" }))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await systemSettingsService.update({ integrations });
            enqueueSnackbar("Integrations saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save integrations", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Integrations"
                subtitle="Tracking/analytics snippets for the public Login, Register, and related pages"
                action={
                    <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || loading}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
                    </Button>
                }
            />

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        Pasted exactly as-is, with no validation — only paste snippets from a provider you trust (Google
                        Tag Manager, Meta, Google Ads, etc.). These only run on the public Login/Register pages, never
                        inside the logged-in admin or customer portal.
                    </Alert>

                    <Card>
                        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <Box>
                                <Typography variant="overline" color="text.secondary" display="block">
                                    Google Tag Manager — Head Snippet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    The first {"<script>"} block GTM gives you, meant for {"<head>"}. Runs on every public
                                    page (Login, Register, Forgot/Reset Password, Verify Email).
                                </Typography>
                                <TextField
                                    placeholder="<!-- Google Tag Manager -->&#10;<script>...</script>&#10;<!-- End Google Tag Manager -->"
                                    value={integrations.gtmHeadSnippet}
                                    onChange={(e) => setIntegrations((p) => ({ ...p, gtmHeadSnippet: e.target.value }))}
                                    fullWidth
                                    multiline
                                    minRows={6}
                                    sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
                                />
                            </Box>

                            <Box>
                                <Typography variant="overline" color="text.secondary" display="block">
                                    Google Tag Manager — Body Snippet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    GTM's {"<noscript>"} fallback, meant for right after the opening {"<body>"} tag. Same
                                    pages as the head snippet above.
                                </Typography>
                                <TextField
                                    placeholder="<!-- Google Tag Manager (noscript) -->&#10;<noscript><iframe ... /></noscript>&#10;<!-- End Google Tag Manager (noscript) -->"
                                    value={integrations.gtmBodySnippet}
                                    onChange={(e) => setIntegrations((p) => ({ ...p, gtmBodySnippet: e.target.value }))}
                                    fullWidth
                                    multiline
                                    minRows={6}
                                    sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
                                />
                            </Box>

                            <Box>
                                <Typography variant="overline" color="text.secondary" display="block">
                                    Lead Conversion Event Snippet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Fired once, only at the actual lead-conversion moment — the Register page's "Check
                                    your email" success screen, right after a new account is created. Use this for a
                                    conversion/event tag (e.g. a Google Ads or Meta Pixel conversion event), not a
                                    site-wide script.
                                </Typography>
                                <TextField
                                    placeholder="<script>gtag('event', 'conversion', { ... });</script>"
                                    value={integrations.leadConversionSnippet}
                                    onChange={(e) => setIntegrations((p) => ({ ...p, leadConversionSnippet: e.target.value }))}
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
};

export default AdminIntegrations;
